import { create } from 'zustand';
import { supabase } from '../config/supabase';
import { Database } from '../config/supabase';

type AdminUser = Database['public']['Tables']['admin_users']['Row'];

interface AuthState {
  user: AdminUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  
  // Actions
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: AdminUser | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  initialize: async () => {
    try {
      set({ isLoading: true });
      
      // Verificar sessão atual
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) throw error;
      
      if (session?.user) {
        // Buscar dados do usuário admin
        let { data: userData, error: userError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('id', session.user.id)
          .single();
        
        // Se o usuário não existe na tabela admin_users, criar automaticamente
        if (userError && userError.code === 'PGRST116') {
          console.log('Usuário não encontrado na tabela admin_users durante inicialização. Criando registro...');
          
          const email = session.user.email || '';
          const defaultRole = email === 'admin@inrock.app' ? 'superadmin' : 'user';
          const defaultPermissions = {
            can_manage_users: defaultRole === 'superadmin',
            can_view_logs: true,
            can_manage_settings: defaultRole === 'superadmin',
            can_view_analytics: true,
          };
          
          // Criar registro na tabela admin_users
          const { data: newUserData, error: createError } = await supabase
            .from('admin_users')
            .insert({
              id: session.user.id,
              email: email,
              role: defaultRole,
              region: 'global',
              is_active: true,
              is_blocked: false,
              permissions: defaultPermissions,
            })
            .select()
            .single();
          
          if (createError) {
            console.error('Erro ao criar usuário admin durante inicialização:', createError);
            // Se não conseguir criar, apenas definir como não inicializado
            set({ isInitialized: true });
            return;
          }
          
          userData = newUserData;
        } else if (userError) {
          console.error('Erro ao buscar usuário admin:', userError);
          set({ isInitialized: true });
          return;
        }
        
        if (userData) {
          // Verificar se o usuário está bloqueado durante inicialização
          if (userData.is_blocked || !userData.is_active) {
            set({ user: null, isInitialized: true });
            return;
          }
          
          // Verificar se o usuário tem permissão para acessar o sistema administrativo
          if (userData.role !== 'superadmin' && userData.role !== 'admin') {
            set({ user: null, isInitialized: true });
            return;
          }
          
          set({ user: userData, isInitialized: true });
        } else {
          set({ isInitialized: true });
        }
      } else {
        set({ isInitialized: true });
      }
    } catch (error) {
      console.error('Erro ao inicializar auth:', error);
      // Não definir erro na inicialização para não bloquear o app
      set({ isInitialized: true });
    } finally {
      set({ isLoading: false });
    }
  },

  signIn: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });
      
      const { data: { user }, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (user) {
        // Primeiro, tentar buscar dados do usuário admin
        let { data: userData, error: userError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('id', user.id)
          .single();
        
        // Se o usuário não existe na tabela admin_users, criar automaticamente
        if (userError && userError.code === 'PGRST116') {
          console.log('Usuário não encontrado na tabela admin_users. Criando registro...');
          
          // Determinar role baseado no email (padrão: superadmin para admin@inrock.app)
          const defaultRole = email === 'admin@inrock.app' ? 'superadmin' : 'user';
          const defaultPermissions = {
            can_manage_users: defaultRole === 'superadmin',
            can_view_logs: true,
            can_manage_settings: defaultRole === 'superadmin',
            can_view_analytics: true,
          };
          
          // Criar registro na tabela admin_users usando o ID do usuário autenticado
          const { data: newUserData, error: createError } = await supabase
            .from('admin_users')
            .insert({
              id: user.id, // Usar o ID do usuário do Auth
              email: user.email || email,
              role: defaultRole,
              region: 'global',
              is_active: true,
              is_blocked: false,
              permissions: defaultPermissions,
            })
            .select()
            .single();
          
          if (createError) {
            console.error('Erro ao criar usuário admin:', createError);
            // Se falhar na criação, tentar buscar por email como fallback
            const { error: emailError } = await supabase
              .from('admin_users')
              .select('*')
              .eq('email', email)
              .single();
            
            if (emailError) {
              throw new Error('Não foi possível criar ou encontrar usuário admin. Verifique as permissões da tabela.');
            }
            
            // Atualizar o ID do registro existente para corresponder ao ID do Auth
            const { data: updatedUserData, error: updateError } = await supabase
              .from('admin_users')
              .update({ id: user.id })
              .eq('email', email)
              .select()
              .single();
            
            if (updateError) {
              throw new Error('Não foi possível sincronizar usuário admin. Entre em contato com o administrador.');
            }
            
            userData = updatedUserData;
          } else {
            userData = newUserData;
          }
        } else if (userError) {
          throw userError;
        }
        
        // Verificar se o usuário está ativo e não está bloqueado
        if (!userData || !userData.is_active) {
          throw new Error('Usuário desativado');
        }
        
        if (userData.is_blocked) {
          throw new Error('Usuário bloqueado. Entre em contato com o administrador.');
        }
        
        // Verificar se o usuário tem permissão para acessar o sistema administrativo
        // Apenas superadmin e admin podem acessar
        if (userData.role !== 'superadmin' && userData.role !== 'admin') {
          throw new Error('Você não tem permissão para acessar o sistema administrativo.');
        }
        
        // Atualizar último login
        await supabase
          .from('admin_users')
          .update({ last_login: new Date().toISOString() })
          .eq('id', userData.id);
        
        // Registrar log de login (com tratamento de erro caso a função não exista)
        try {
          await supabase.rpc('log_admin_action', {
            p_admin_id: userData.id,
            p_action: 'user_login',
            p_details: { method: 'email_password', ip: 'client_ip' },
            p_severity: 'info'
          });
        } catch (logError) {
          // Se a função não existir, apenas logar no console
          console.warn('Função log_admin_action não disponível:', logError);
        }
        
        set({ user: userData });
      }
    } catch (error: any) {
      console.error('Erro no login:', error);
      set({ error: error.message || 'Erro ao fazer login' });
    } finally {
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true });
      
      // Obter usuário atual do estado antes de fazer logout
      const currentUser = useAuthStore.getState().user;
      
      // Registrar log de logout se houver usuário
      if (currentUser) {
        try {
          await supabase.rpc('log_admin_action', {
            p_admin_id: currentUser.id,
            p_action: 'user_logout',
            p_details: { method: 'manual' },
            p_severity: 'info'
          });
        } catch (logError) {
          // Se a função não existir, apenas logar no console
          console.warn('Função log_admin_action não disponível:', logError);
        }
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      set({ user: null });
    } catch (error: any) {
      console.error('Erro no logout:', error);
      set({ error: error.message || 'Erro ao fazer logout' });
    } finally {
      set({ isLoading: false });
    }
  },

  setUser: (user: AdminUser | null) => {
    set({ user });
  },

  clearError: () => {
    set({ error: null });
  },
}));
